import React from 'react';
import PropTypes from 'prop-types';

/**
 * NeoAlert
 * Componente de alerta premium unificado do NeoApp.
 * Mantém consistência visual com a identidade "Taça Outono" e "Outfit" do design system.
 * Características: fundo caramelo-soft (bg-light-soft), cantos arredondados (rounded-3),
 * sombra suave (shadow-sm), sem bordas padrão, borda lateral decorativa dinâmica de 4px,
 * e um ícone em contêiner circular com destaque tonal.
 */
const NeoAlert = ({
    color = 'info', // 'info' | 'warning' | 'success' | 'danger' | 'primary'
    title = null,
    icon = null, // Custom icon (e.g. 'mdi-information-outline'). If null, falls back to standard color icons
    animate = true,
    borderLeftAccent = true,
    className = '',
    style = {},
    children,
    ...props
}) => {
    // Default icons mapping
    const defaultIcons = {
        primary: 'mdi-information-outline',
        info: 'mdi-information-outline',
        warning: 'mdi-alert-outline',
        success: 'mdi-check-circle-outline',
        danger: 'mdi-alert-circle-outline'
    };

    // Default icon colors mapping
    const defaultIconColors = {
        primary: 'var(--color-brand-primary, #482712)',
        info: 'var(--color-brand-accent, #e2661c)',
        warning: '#f1b44c',
        success: '#0ab39c',
        danger: '#f06548'
    };

    // Border Left Accent colors mapping
    const borderColors = {
        primary: 'var(--color-brand-primary, #482712)',
        info: 'var(--color-brand-accent, #e2661c)',
        warning: '#f1b44c',
        success: '#0ab39c',
        danger: '#f06548'
    };

    const chosenIcon = icon || defaultIcons[color] || 'mdi-information-outline';
    const iconColor = defaultIconColors[color] || 'var(--color-brand-accent, #e2661c)';
    const borderColor = borderColors[color] || 'var(--color-brand-accent, #e2661c)';

    const alertStyle = {
        fontFamily: 'Outfit, sans-serif',
        borderLeft: borderLeftAccent ? `4px solid ${borderColor}` : 'none',
        ...style
    };

    const alertClasses = [
        'alert',
        `alert-${color}`,
        'rounded-3',
        'py-3',
        'px-4',
        'mb-4',
        'shadow-sm',
        'd-flex',
        'align-items-center',
        'gap-3',
        'border-0',
        'bg-light-soft',
        animate ? 'animate__animated animate__fadeIn' : '',
        className
    ].filter(Boolean).join(' ');

    return (
        <div className={alertClasses} role="alert" style={alertStyle} {...props}>
            <div 
                className="flex-shrink-0 bg-white rounded-circle d-flex align-items-center justify-content-center shadow-sm animate__animated animate__zoomIn animate__faster" 
                style={{ 
                    width: '48px', 
                    height: '48px', 
                    border: '1px solid rgba(72, 39, 18, 0.1)' 
                }}
            >
                <i className={`mdi ${chosenIcon} fs-4`} style={{ color: iconColor }}></i>
            </div>
            <div>
                {title && (
                    <h5 className="alert-heading font-size-14 mb-1 fw-bold text-dark">
                        {title}
                    </h5>
                )}
                {children && (
                    <div className="mb-0 small text-muted">
                        {children}
                    </div>
                )}
            </div>
        </div>
    );
};

NeoAlert.propTypes = {
    color: PropTypes.oneOf(['primary', 'info', 'warning', 'success', 'danger']),
    title: PropTypes.string,
    icon: PropTypes.string,
    animate: PropTypes.bool,
    borderLeftAccent: PropTypes.bool,
    className: PropTypes.string,
    style: PropTypes.object,
    children: PropTypes.node
};

export default NeoAlert;
